'use no memo';

import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';

import {isEmpty, isNotEmpty} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {GetVehicleAlbumPhotoListApiArg, selectVehicleAlbumPhotoList} from '@dms/api/sales';

const VEHICLE_ALBUM_POLLING_INTERVAL = 5000;
const MAX_VEHICLE_ALBUM_POLLING_ATTEMPTS = 7;
const MAX_VEHICLE_ALBUM_POLLING_DURATION =
  VEHICLE_ALBUM_POLLING_INTERVAL * MAX_VEHICLE_ALBUM_POLLING_ATTEMPTS;

/**
 * Drives the polling interval for the vehicle album photo list while media is still being
 * processed by the backend. A freshly uploaded video first appears with `mediaType: 'unknown'`,
 * then transitions to `'video'` but may still lack a `thumbnailUrl`.
 */
export const useVehicleAlbumPhotoListInterval = (queryArg: GetVehicleAlbumPhotoListApiArg) => {
  const cached = useSelector(selectVehicleAlbumPhotoList(queryArg));

  const processingMediaKey = (cached.data ?? [])
    .filter(
      (image) =>
        image.mediaType === 'unknown' ||
        (image.mediaType === 'video' && isNilOrEmpty(image.thumbnailUrl))
    )
    .map((image) => image.id)
    .sort()
    .join(',');

  const isAlbumProcessing = isNotEmpty(processingMediaKey);

  const [hasPollingTimedOut, setHasPollingTimedOut] = useState(false);

  useEffect(
    function restartVehicleAlbumPollingDeadline() {
      if (isEmpty(processingMediaKey)) {
        return undefined;
      }

      const timeoutId = setTimeout(
        () => setHasPollingTimedOut(true),
        MAX_VEHICLE_ALBUM_POLLING_DURATION
      );

      return () => {
        clearTimeout(timeoutId);
        setHasPollingTimedOut(false);
      };
    },
    [processingMediaKey]
  );

  const pollingInterval =
    isAlbumProcessing && !hasPollingTimedOut ? VEHICLE_ALBUM_POLLING_INTERVAL : 0;

  return {pollingInterval};
};
